import React from "react"
import { FooterMenuFieldsFragment, HeaderMenuFieldsFragment } from "../../../graphql-types"
import * as styles from "./website-map.module.scss"

type RenderProps = {
  menuData: HeaderMenuFieldsFragment & FooterMenuFieldsFragment
  locale?: string
  defaultLocale?: string
}

type MenuSection = NonNullable<NonNullable<HeaderMenuFieldsFragment["menuSection"]>[number]>
type Submenu = NonNullable<MenuSection["subMenu"]>
type SubmenuItem = NonNullable<Submenu[number]>
type DisplayedSubmenuItem = SubmenuItem & { page: NonNullable<SubmenuItem["page"]> }

const WebsiteMap: React.FC<RenderProps> = ({ menuData, locale, defaultLocale }) => {
  // Build a slug link prefixed with the active locale, except for the default
  // locale which is served at the root. Keeps sitemap links consistent with the
  // rest of the app so language switching does not drop the locale prefix.
  const buildLocalizedHref = (slug: string) =>
    locale && defaultLocale && locale !== defaultLocale ? `/${locale}/${slug}` : `/${slug}`

  const buildWebsiteMapMenu = (
    key: string,
    mainMenuLabel: string | null | undefined,
    mainMenuPage: MenuSection["page"],
    submenu: MenuSection["subMenu"]
  ) => {
    const shouldDisplaySubmenu = (submenuItem: SubmenuItem): submenuItem is DisplayedSubmenuItem => {
      return !!(submenuItem.page && (submenuItem.headerDisplay || submenuItem.footerDisplay))
    }
    const displayedSubmenu = (submenu || [])
      .filter((submenuItem): submenuItem is SubmenuItem => !!submenuItem)
      .filter(shouldDisplaySubmenu)

    return (
      <li className={styles.menu} key={key}>
        {mainMenuPage && mainMenuPage.slug ? (
          <h2 className={styles.mainMenuLabel}>
            <a href={buildLocalizedHref(mainMenuPage.slug)}>
              <span className={styles.mainMenuChevron} aria-hidden="true">
                &gt;
              </span>
              {mainMenuLabel}
            </a>
          </h2>
        ) : (
          <h2 className={styles.mainMenuLabel}>
            <span className={styles.mainMenuChevron} aria-hidden="true">
              &gt;
            </span>
            {mainMenuLabel}
          </h2>
        )}
        {displayedSubmenu.length > 0 && (
          <ul className={styles.submenu}>
            {displayedSubmenu.map((submenuItem, index) => (
              <li key={index}>
                <a className={styles.submenulabel} href={buildLocalizedHref(submenuItem.page.slug)}>
                  {submenuItem.label}
                </a>
              </li>
            ))}
          </ul>
        )}
      </li>
    )
  }

  const menuSections = (menuData.menuSection || []).filter((menuSection): menuSection is MenuSection => !!menuSection)

  return (
    <ul className={styles.websiteMap}>
      {menuSections.map(
        (menuSection, index) =>
          (menuSection.headerDisplay || menuSection.footerDisplay) &&
          buildWebsiteMapMenu(String(index), menuSection.label, menuSection.page, menuSection.subMenu)
      )}
    </ul>
  )
}

export default WebsiteMap
